import Vue from 'vue';
import router from '../router'
import {v4 as uuidv4} from 'uuid'
import { startNewTireReport, getWorkshops, submitNewTireReport, getAssetTireReports } from "../api/api"
import { saveIncompleReport, deleteIncompleReport, getIncompleteReport } from "../lib/incompletereports"

// initial state
const initialState = {
  regnoError: false,
  workshops: [],
  asset: null,
  newTireReport: null,
  lastServicedTires: null,
}
// state
const state = { ...initialState }

// getters
const getters = {
  workshops: (state) => {
    return state.workshops.map((workshop) => {
      workshop.searchLabel = `${workshop.name} ${workshop.city}`.toLowerCase()
      workshop.label = `${workshop.name} (${workshop.city}, ${workshop.countryCode})`
      return workshop
    }).sort((a, b) => {
      return a.searchLabel > b.searchLabel
    })
  }
}

// actions
const actions = {
  async startTireReport({ commit }, registrationNumber) {
    try {
      const friendlyRegistrationNumber = registrationNumber.replace(/[^A-Za-z0-9]+/g, "");
      if (!friendlyRegistrationNumber) throw new Error("No regno after normalization.")
      const { asset } = await startNewTireReport(friendlyRegistrationNumber)
      const workshops = await getWorkshops()

      await getAssetTireReports(asset.assetId)
        .then(data => commit("setLastServicedTires", data.tireReports))
        .catch(err => console.log(`Could not get last tire report: ${err}`)) // catch errors for last tire report without breaking flow
      
      const tireReportId = `tirereport-${uuidv4()}`
      const startedAt = Vue.moment().toISOString()
      commit("startNewReport", {tireReportId, asset, workshops, startedAt})
      return router.push({ name: "trailer", params: { id: tireReportId } });
    } catch (err) {
      commit("resetState");
      commit('setRegnoError', true)
      router.push({ name: "frontpage" });
      console.log(err)
    }
  },

  async continueIncompleteReport({ commit }, tireReportId) {
    const incomepleteReport = getIncompleteReport(tireReportId);
    if (!incomepleteReport || !incomepleteReport.registrationNumber) {
      deleteIncompleReport(tireReportId)
      commit("resetState");
      return router.push({ name: "frontpage" });
    }
    try {
      const { asset } = await startNewTireReport(incomepleteReport.registrationNumber)
      const workshops = await getWorkshops()

      await getAssetTireReports(asset.assetId)
        .then(data => commit("setLastServicedTires", data))
        .catch(err => console.log(`Could not get last tire report: ${err}`)) // catch errors for last tire report without breaking flow
        
      commit("startNewReport", {
        tireReportId, 
        asset, 
        workshops, 
        startedAt: incomepleteReport.startedAt
      })
      if (incomepleteReport.remark) commit("addRemark", incomepleteReport.remark)
      if (incomepleteReport.workshop) commit("addWorkshopData", incomepleteReport.workshop)
      if (incomepleteReport.tires) Object.keys(incomepleteReport.tires).forEach((tirePosition) => commit("addTireData", {
        tirePosition: tirePosition,
        tireData: incomepleteReport.tires[tirePosition]
      }))
    } catch (err) {
      deleteIncompleReport(tireReportId)
      commit("resetState");
      router.push({ name: "frontpage" });
      Vue.noty.error("Error continuing incomplete report.");
      console.log(err)
    }
  },

  async submitNewTireReport({ state }) {
    try {
      await submitNewTireReport(state.asset.assetId, state.newTireReport)
      deleteIncompleReport(state.newTireReport.tireReportId)
      router.push({ name: "completed", params: {id: state.newTireReport.tireReportId} });
    } catch (err) {
      Vue.noty.error("Error submitting tire report.");
      console.log(err)
    }
  },
  cancelTireReport({ state, commit }) {
    if (state.newTireReport && state.newTireReport.tireReportId) {
      deleteIncompleReport(state.newTireReport.tireReportId)
    }
    commit("resetState");
    return router.push({ name: "frontpage" });
  },
  setRegnoError({ commit }, isError) {
    commit('setRegnoError', isError)
  },
  addTireData({ commit, state }, data) {
    commit("addTireData", data)
    saveIncompleReport(state.newTireReport)
  },
  addWorkshopData({ commit }, data) {
    commit("addWorkshopData", data)
    saveIncompleReport(state.newTireReport)
  },
  addAttachmentData({ commit }, data) {
    commit("addAttachmentData", data)
    saveIncompleReport(state.newTireReport)
  },
  addRemark({ commit }, remark) {
    commit("addRemark", remark)
    saveIncompleReport(state.newTireReport)
  },
  resetState({ commit }) {
    commit("resetState");
  }
}

// mutations
const mutations = {
  setRegnoError(state, isError) {
    state.regnoError = isError;
  },
  startNewReport(state, data) {
    state.newTireReport = {
      tireReportId: data.tireReportId,
      startedAt: data.startedAt,
      registrationNumber: data.asset.registrationNumber,
    };
    state.asset = data.asset;
    state.workshops = data.workshops;
  },
  addTireData(state, data) {
    state.newTireReport = {
      ...state.newTireReport,
      tires: {
        ...state.newTireReport.tires,
        [data.tirePosition]: data.tireData
      }
    }
  },
  addWorkshopData(state, data) {
    state.newTireReport = {
      ...state.newTireReport,
      workshop: { ...data },
    }
  },
  addAttachmentData(state, data) {
    state.newTireReport = {
      ...state.newTireReport,
      attachments: { ...data },
    }
  },
  addRemark(state, remark) {
    state.newTireReport = {
      ...state.newTireReport,
      remark: remark,
    }
  },
  resetState(state) {
    const s = { ...initialState }
    Object.keys(s).forEach(key => {
      state[key] = s[key]
    })
  },
  setLastServicedTires(state, tireReports){
    if(!tireReports || !tireReports.length) return
    // We create an object where key is tire position and value is tire data.
    // The reports are sorted by date, oldest first. When iterating, we write over tire position if we have data in a newer report
    state.lastServicedTires = tireReports.reduce((acc, cur) => ({ ...acc, ...getTirePositionReportData(cur) }), {})
},
}

const getTirePositionReportData = tireReport => {
  if (!tireReport || !tireReport.vehicle || !tireReport.vehicle.tires) return {}

  return tireReport.vehicle.tires.reduce((acc, cur) => { 
      if (cur.fittedTire) return { ...acc, [cur.position]: cur.fittedTire }
      if (cur.checkedTire) return { ...acc, [cur.position]: cur.checkedTire }
      return acc
    }, {})
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
